import React, { useCallback, useEffect, useMemo, useState } from 'react';
import TOKEN_BUNDLES, { findBundleById, findBundleByTokens } from '../constants/tokenBundles';
import { useCheckout } from '../features/checkout';
import { useAuth } from '../features/auth';
import { useToast } from '../features/toast';
import { auth } from '../services/firebase';

const MobileTokenPanel = ({ onClose }) => {
  const { tokenCheckoutContext, beginTokenCheckout, dismissTokenCheckout } = useCheckout();
  const { resendVerification } = useAuth();
  const { showToast } = useToast();
  const [view, setView] = useState('select');
  // Filter out first-spin bundles for display
  const regularBundles = useMemo(
    () => TOKEN_BUNDLES.filter((bundle) => !bundle.id.startsWith('first-spin-')),
    []
  );
  const [selectedBundleId, setSelectedBundleId] = useState(regularBundles[0]?.id || null);
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);

  const selectedBundle = useMemo(
    () => (selectedBundleId && findBundleById(selectedBundleId)) || regularBundles[0],
    [selectedBundleId, regularBundles],
  );

  const resolvedSuccessBundle = useMemo(() => {
    return (
      (tokenCheckoutContext?.purchase?.bundleId &&
        findBundleById(tokenCheckoutContext.purchase.bundleId)) ||
      (tokenCheckoutContext?.bundleId && findBundleById(tokenCheckoutContext.bundleId)) ||
      (tokenCheckoutContext?.purchase?.tokens &&
        findBundleByTokens(tokenCheckoutContext.purchase.tokens)) ||
      null
    );
  }, [tokenCheckoutContext]);

  useEffect(() => {
    if (!tokenCheckoutContext) {
      return;
    }
    setIsCheckoutLoading(false);
    if (tokenCheckoutContext.status === 'pending') {
      setView('processing');
      return;
    }
    if (tokenCheckoutContext.status === 'success') {
      setView('success');
      return;
    }
    if (tokenCheckoutContext.status === 'cancel') {
      setView('cancel');
      return;
    }
    if (tokenCheckoutContext.status === 'error') {
      setView('error');
      return;
    }
  }, [tokenCheckoutContext]);

  const disableSelection = view === 'processing' || isCheckoutLoading;

  const handleSelectBundle = useCallback(
    (bundle) => {
      if (!bundle || !bundle.id || disableSelection) return;
      setSelectedBundleId(bundle.id);
      setView('select');
    },
    [disableSelection],
  );

  const handleCheckout = useCallback(async () => {
    if (!selectedBundle || disableSelection) return;
    const currentUser = auth.currentUser;
    if (!currentUser) {
      showToast({ message: 'Sign in to purchase tokens.', tone: 'info' });
      return;
    }
    try {
      await currentUser.reload();
    } catch (_) {}
    if (!auth.currentUser?.emailVerified) {
      showToast({
        message: (
          <>
            Verify your email to unlock all features.&nbsp;
            <button
              type="button"
              onClick={() => {
                try {
                  resendVerification?.();
                } catch (_) {}
              }}
              className="font-semibold underline underline-offset-2 hover:opacity-80"
            >
              Resend.
            </button>
          </>
        ),
        tone: 'info',
      });
      return;
    }
    setIsCheckoutLoading(true);
    setView('processing');
    try {
      await beginTokenCheckout(selectedBundle);
    } catch (err) {
      setIsCheckoutLoading(false);
      setView('error');
    }
  }, [selectedBundle, disableSelection, beginTokenCheckout, showToast, resendVerification]);

  const handleClose = useCallback(() => {
    dismissTokenCheckout();
    onClose?.();
    setView('select');
    setIsCheckoutLoading(false);
  }, [dismissTokenCheckout, onClose]);

  const renderHandle = () => (
    <div className="flex flex-col items-center gap-3">
      <button
        type="button"
        onClick={handleClose}
        aria-label="Close token panel"
        className="h-1.5 w-10 rounded-full bg-ponpon-navy/15 transition hover:bg-ponpon-navy/25 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ponpon-coral/60"
      />
      <div className="w-full text-left">
        <h4 className="text-sm font-semibold text-ponpon-navy">Top up your tokens</h4>
      </div>
    </div>
  );

  const renderBundleList = () => (
    <div className="flex flex-col gap-3">
      {TOKEN_BUNDLES.filter((bundle) => !bundle.id.startsWith('first-spin-')).map((bundle) => {
        const isActive =
          selectedBundleId ? selectedBundleId === bundle.id : bundle.id === selectedBundle.id;
        return (
          <button
            type="button"
            key={bundle.id}
            onClick={() => handleSelectBundle(bundle)}
            disabled={disableSelection}
            className={[
              'group flex items-center justify-between rounded-2xl border px-4 py-3 text-left text-sm text-ponpon-navy shadow-sm transition',
              isActive
                ? 'border-ponpon-coral/60 bg-ponpon-sky/20 text-ponpon-coral'
                : 'border-ponpon-navy/10 bg-white hover:border-ponpon-coral/50 hover:text-ponpon-coral',
              disableSelection ? 'opacity-70' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <span className="font-semibold">
              {bundle.tokens} token{bundle.tokens === 1 ? '' : 's'}
            </span>
            <span className="text-sm font-semibold text-ponpon-coral group-hover:text-[#E23A32]">
              ${bundle.price}
            </span>
          </button>
        );
      })}
    </div>
  );

  const renderSelectView = () => (
    <>
      {renderBundleList()}
      <p className="px-1 text-[0.65rem] leading-snug text-ponpon-navy/60">
        Secure checkout via Stripe. Tokens appear as soon as payment succeeds.
      </p>
      <button
        type="button"
        disabled={!selectedBundle || disableSelection}
        onClick={handleCheckout}
        className={[
          'flex items-center justify-center rounded-full bg-ponpon-coral px-4 py-3 text-sm font-semibold text-white transition hover:bg-[#E23A32]',
          (!selectedBundle || disableSelection) &&
            'cursor-not-allowed opacity-70 hover:bg-ponpon-coral',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {isCheckoutLoading || view === 'processing' ? 'Redirecting…' : 'Checkout'}
      </button>
    </>
  );

  const renderStatusCard = (emoji, title, body, accentClass = 'text-ponpon-navy/70') => (
    <div className="flex flex-col items-center gap-2.5 rounded-3xl border border-ponpon-navy/10 bg-white/95 px-4 py-6 text-center text-ponpon-navy">
      <div className="text-4xl" aria-hidden="true">
        {emoji}
      </div>
      <span className={`text-sm font-semibold uppercase tracking-wide ${accentClass}`}>{title}</span>
      {body ? <p className="text-sm text-ponpon-navy/70">{body}</p> : null}
    </div>
  );

  const renderSuccessView = () =>
    renderStatusCard(
      '🎉',
      'Success',
      resolvedSuccessBundle
        ? `${resolvedSuccessBundle.tokens} token${resolvedSuccessBundle.tokens === 1 ? '' : 's'} added for $${resolvedSuccessBundle.price}.`
        : 'Tokens added to your balance.',
      'text-ponpon-coral/70',
    );

  const renderProcessingView = () =>
    renderStatusCard('⏳', 'Processing', 'Complete checkout in the new tab to finish.');

  const renderCancelView = () =>
    renderStatusCard('🛑', 'Checkout cancelled', 'No worries — you can try again any time.');

  const renderErrorView = () =>
    renderStatusCard('⚠️', 'Something went wrong', 'Please try again or contact support.');

  const renderBody = () => {
    if (view === 'select') return renderSelectView();
    if (view === 'success') return renderSuccessView();
    if (view === 'processing') return renderProcessingView();
    if (view === 'cancel') return renderCancelView();
    if (view === 'error') return renderErrorView();
    return null;
  };

  return (
    <section className="flex flex-col gap-4" aria-label="Mobile token purchase panel">
      {renderHandle()}
      <div className="flex max-h-[60vh] flex-col gap-4 overflow-y-auto pr-1">{renderBody()}</div>
    </section>
  );
};

export default MobileTokenPanel;
